/* eslint-disable react-dom/no-missing-iframe-sandbox */
import { zodResolver } from "@hookform/resolvers/zod";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useEffect, useState } from "react"; // Need useState for now as we're transitioning to the store
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { z } from "zod";

import { Button } from "@/newComponents/button/Button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/newComponents/form/Form";
import { IconBin, IconInputSearch } from "@/newComponents/icon/Icon.tsx";
import { FormInput } from "@/newComponents/input/FormInput.tsx";
import Heading from "@/newComponents/typography/Heading";
import Text from "@/newComponents/typography/Text.tsx";
import { useUIStateStore } from "@/store/uiStateStore.ts";

// Import types
import type {
  SpotifyAlbumInfoResponse,
  SpotifyAlbumTracksResponse,
  SpotifyErrorResponse,
  SpotifyMultipleTracksResponse,
  SpotifyTokenResponse,
  SpotifyTrackItem,
} from "./types/spotify";

import useAppStore from "../../../../../store/appStateZustand.ts";
import AddAlbumWizardNavigation from "./components/AddAlbumWizardNavigation.tsx";
import AddAlbumWizardStepProgress from "./components/AddAlbumWizardStepProgress.tsx";
import AssignWritersStep from "./steps/AssignWritersStep";
import ChooseWritersStep from "./steps/ChooseWritersStep";
import DetailsAndSharesStep from "./steps/DetailsAndSharesStep";
import SelectSongsStep from "./steps/SelectSongsStep";
import SummaryAndSubmitStep from "./steps/SummaryAndSubmitStep";
import { useAddAlbumWizardStore } from "./store/addAlbumWizardStore.ts";
import useAlbumStore from "./store/albumStore";
import useWriterStore from "./store/writerStore";
import { validateAllSongsHaveWriters } from "./utils/validation";

export default function SongsAddAlbum() {
  const isTunecore = useUIStateStore(state => state.isTunecore);
  const flags = useFlags();
  const newAddSongs = flags["pd-add-songs-rework"] as boolean;
  // const { highlightColour, highlightColour2 } = useGetColours();
  const { t } = useTranslation("album");

  // Zod schema for Spotify URL validation - moved inside component to access t function
  const spotifyAlbumSchema = z.object({
    spotifyUrl: z
      .string()
      .min(1, t("album:enter-spotify-album-url") ?? "Please enter a Spotify album URL")
      .refine(
        (url) => {
          // Enhanced regex pattern for Spotify album URLs
          const spotifyAlbumRegex = /^https:\/\/open\.spotify\.com\/album\/[a-zA-Z0-9]{22}(?:\?.*)?$/;
          return spotifyAlbumRegex.test(url);
        },
        {
          message: t("album:enter-valid-spotify-album-url") ?? "Please enter a valid Spotify album URL",
        },
      ),
  });

  type SpotifyAlbumFormData = z.infer<typeof spotifyAlbumSchema>;

  // --- BEGIN SPOTIFY STATE AND LOGIC ---
  const [spotifyURL, setSpotifyURL] = useState("");
  const [spotifyResult, setSpotifyResult] = useState<SpotifyAlbumInfoResponse | null>(null);
  const [showIframeSkeleton, setShowIframeSkeleton] = useState(false);

  const albumStore = useAlbumStore();
  const {
    setAlbumData,
    setTracks,
    setApiToken,
    setIsLoading,
    setError: setErrorMsg,
    apiToken,
    tracks,
    isLoading,
    error: errorMsg,
  } = albumStore;

  // Keep the albumName for backward compatibility during refactoring
  const [_albumName, setAlbumName] = useState<string | null>(null);

  // Setup react-hook-form with Zod validation
  const form = useForm<SpotifyAlbumFormData>({
    resolver: zodResolver(spotifyAlbumSchema),
    defaultValues: {
      spotifyUrl: "",
    },
  });

  const getSpotifyAPIToken = async (): Promise<string> => {
    // Use the API URL from the store or use a default if not available
    const apiUrl = useAppStore.getState().apiUrl || "";
    if (apiUrl === "") {
      setErrorMsg(t("album:api-url-not-configured"));
      throw new Error(t("album:api-url-not-configured") ?? "API URL not configured");
    }
    try {
      const response = await fetch(`${apiUrl}/spotify-token`);

      if (!response.ok) {
        setErrorMsg( // Changed to setErrorMsg
          t("album:spotify-token-api-error", { status: response.status }),
        );
        throw new Error(
          t("album:spotify-token-api-error", { status: response.status }) ?? `Spotify token API error! status: ${response.status}`,
        );
      }
      const data = (await response.json()) as SpotifyTokenResponse;
      if (data.access_token) {
        return data.access_token;
      }
      setErrorMsg(t("album:failed-retrieve-token-spotify")); // Changed to setErrorMsg
      throw new Error(t("album:failed-retrieve-token-spotify") ?? "Failed to retrieve token from Spotify API response");
    }
    catch (error) {
      console.error("Error fetching Spotify token:", error);
      setErrorMsg( // Changed to setErrorMsg
        t("album:failed-fetch-spotify-token", {
          error: error instanceof Error ? error.message : t("album:unknown-error") ?? "Unknown error",
        }),
      );
      throw error;
    }
  };

  const fetchAlbumData = async (url?: string) => {
    // Use provided URL or fallback to state
    const urlToUse = url !== undefined && url !== null ? url : spotifyURL;

    // Don't fetch if no URL is provided
    if (!urlToUse || urlToUse.trim() === "") {
      return;
    }

    setIsLoading(true);
    setErrorMsg(null);

    let currentToken = apiToken;
    if (currentToken === null || currentToken === "") {
      try {
        currentToken = await getSpotifyAPIToken();
        setApiToken(currentToken); // Store the fetched token
      }
      catch { // Removed unused _error
        // Error message is already set by getSpotifyAPIToken
        setIsLoading(false);
        return;
      }
    }

    if (currentToken === null || currentToken === "") {
      setIsLoading(false);
      if (errorMsg == null) {
        setErrorMsg("Spotify API token is unavailable.");
      }
      return;
    }

    try {
      const urlParts = urlToUse.split("/");
      const albumId = urlParts[urlParts.length - 1].split("?")[0];

      if (albumId === "" || albumId === undefined) {
        throw new Error(t("album:could-not-parse-album-id") ?? "Could not parse Album ID from URL.");
      }

      // Fetch album info (for album name)
      let albumInfoResponse = await fetch(`https://api.spotify.com/v1/albums/${albumId}`, {
        headers: { Authorization: `Bearer ${currentToken}` },
      });

      if (!albumInfoResponse.ok) {
        if (albumInfoResponse.status === 401) { // Token might have expired
          try {
            currentToken = await getSpotifyAPIToken();
            setApiToken(currentToken); // Store the refreshed token
          }
          catch { // Removed unused _error
            // Error message is already set by getSpotifyAPIToken
            setIsLoading(false);
            throw new Error(`Spotify Album Info API error! status: ${albumInfoResponse.status} (token refresh failed)`);
          }

          if (currentToken !== null && currentToken !== "") {
            albumInfoResponse = await fetch(`https://api.spotify.com/v1/albums/${albumId}`, {
              headers: { Authorization: `Bearer ${currentToken}` },
            });
            if (!albumInfoResponse.ok) {
              const errorData = await albumInfoResponse.json() as SpotifyErrorResponse;
              // Corrected template literal
              throw new Error(t("album:spotify-album-info-api-error-retry", {
                status: albumInfoResponse.status,
                message: errorData?.error?.message ?? t("album:unknown-error") ?? "Unknown error",
              }) ?? `Spotify Album Info API error (after retry)! status: ${albumInfoResponse.status}, message: ${errorData?.error?.message ?? "Unknown error"}`);
            }
          }
          else {
            // If token is still null/empty after refresh attempt, error out.
            setIsLoading(false);
            if (errorMsg == null) { // Check if getSpotifyAPIToken set an error
              setErrorMsg(t("album:spotify-token-unavailable-after-refresh"));
            }
            return;
          }
        }
        else {
          const errorData = await albumInfoResponse.json() as SpotifyErrorResponse;
          // Corrected template literal
          throw new Error(`Spotify Album Info API error! status: ${albumInfoResponse.status}, message: ${errorData?.error?.message || "Unknown error"}`);
        }
      }

      const albumInfoData = await albumInfoResponse.json() as SpotifyAlbumInfoResponse;
      // Set both in state and in Zustand store
      setAlbumName(albumInfoData.name);
      setAlbumData(albumInfoData);
      setSpotifyResult(albumInfoData);
      // Show skeleton for 3 seconds when new result is loaded
      setShowIframeSkeleton(true);

      // First fetch basic track info from album tracks endpoint
      const tracksResponse = await fetch(`https://api.spotify.com/v1/albums/${albumId}/tracks`, {
        headers: { Authorization: `Bearer ${currentToken}` },
      });
      if (!tracksResponse.ok) {
        const errorData = await tracksResponse.json() as SpotifyErrorResponse;
        throw new Error(`Spotify Album Tracks API error! status: ${tracksResponse.status}, message: ${errorData?.error?.message || "Unknown error"}`);
      }
      const tracksData = await tracksResponse.json() as SpotifyAlbumTracksResponse;

      // Collect all track IDs
      const trackIds = tracksData.items.map(track => track.id);
      console.log(`Fetched ${trackIds.length} track IDs from album:`, trackIds);

      // Fetch full track details with ISRCs using the multiple tracks endpoint
      // Spotify API allows up to 50 track IDs per request
      const trackChunks: string[][] = [];
      for (let i = 0; i < trackIds.length; i += 50) {
        trackChunks.push(trackIds.slice(i, i + 50));
      }

      const fullTracksData: SpotifyTrackItem[] = [];
      for (const chunk of trackChunks) {
        const idsParam = chunk.join(",");
        let fullTracksResponse = await fetch(`https://api.spotify.com/v1/tracks?ids=${encodeURIComponent(idsParam)}`, {
          headers: { Authorization: `Bearer ${currentToken}` },
        });

        // Handle token refresh if needed
        if (!fullTracksResponse.ok && fullTracksResponse.status === 401) {
          try {
            currentToken = await getSpotifyAPIToken();
            setApiToken(currentToken);
            fullTracksResponse = await fetch(`https://api.spotify.com/v1/tracks?ids=${encodeURIComponent(idsParam)}`, {
              headers: { Authorization: `Bearer ${currentToken}` },
            });
          }
          catch {
            throw new Error(`Spotify Tracks API token refresh failed`);
          }
        }

        if (!fullTracksResponse.ok) {
          const errorData = await fullTracksResponse.json() as SpotifyErrorResponse;
          throw new Error(`Spotify Tracks API error! status: ${fullTracksResponse.status}, message: ${errorData?.error?.message || "Unknown error"}`);
        }

        const fullTracksResponseJson: unknown = await fullTracksResponse.json();

        // Type validation for the response
        if (fullTracksResponseJson == null || typeof fullTracksResponseJson !== "object") {
          throw new TypeError("Invalid response format from Spotify tracks API");
        }

        const fullTracksResult = fullTracksResponseJson as SpotifyMultipleTracksResponse;

        // Validate the tracks array exists
        if (!Array.isArray(fullTracksResult.tracks)) {
          throw new TypeError("Invalid tracks data received from Spotify API");
        }

        const validTracks = fullTracksResult.tracks.filter((track): track is SpotifyTrackItem => track !== null);
        fullTracksData.push(...validTracks);
      }

      console.log(`Successfully fetched full details for ${fullTracksData.length} tracks with ISRCs:`, fullTracksData.map(track => ({
        id: track.id,
        name: track.name,
        isrc: track.external_ids?.isrc,
        isrcStatus: track.external_ids?.isrc != null && track.external_ids.isrc !== "" ? "✅ HAS ISRC" : "❌ NO ISRC",
        releaseDate: track.album?.release_date,
        hasReleaseDate: track.album?.release_date != null ? "✅ HAS RELEASE DATE" : "❌ NO RELEASE DATE",
      })));

      console.log(`Album metadata:`, {
        albumName: albumInfoData.name,
        albumLabel: albumInfoData.label,
        albumReleaseDate: albumInfoData.release_date,
        labelStatus: albumInfoData.label != null && albumInfoData.label !== "" ? "✅ HAS LABEL" : "❌ NO LABEL",
      });

      // Set the enhanced tracks data (with ISRCs) in the store
      setTracks(fullTracksData);
    }
    catch (e) { // Changed from any to unknown
      console.error("Failed to fetch album data:", e);
      if (e instanceof Error) {
        setErrorMsg(`Failed to fetch album data: ${e.message}`);
      }
      else {
        setErrorMsg("Failed to fetch album data due to an unknown error.");
      }
      setAlbumName(null);
      setAlbumData(null);
      setTracks([]);
    }
    finally {
      setIsLoading(false);
    }
  };

  // Remove the automatic fetching on mount since we want user to input URL first
  // useEffect(() => {
  //   void fetchAlbumData();
  // // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, []); // Fetch on mount

  // Handle iframe skeleton timer - show for exactly 3 seconds
  useEffect(() => {
    if (showIframeSkeleton) {
      const timer = setTimeout(() => {
        setShowIframeSkeleton(false);
      }, 1800); // 3 seconds

      return () => clearTimeout(timer);
    }
  }, [showIframeSkeleton]);

  const { currentStep, markStepComplete, nextStep } = useAddAlbumWizardStore();
  const { selectedWriters } = useWriterStore();

  // Handle step completion
  const handleAlbumImportComplete = () => {
    markStepComplete("album-import");
    nextStep();
  };

  const handleSelectSongsComplete = () => {
    markStepComplete("select-songs");
    nextStep();
  };

  const handleChooseWritersComplete = () => {
    markStepComplete("choose-writers");
    nextStep();
  };

  const handleAssignWritersComplete = () => {
    const validation = validateAllSongsHaveWriters();

    if (!validation.isValid) {
      toast.error(`Cannot proceed: ${validation.errorMessage}`, {
        position: "top-right",
        autoClose: 6000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      return; // Don't proceed to next step
    }

    markStepComplete("assign-writers");
    nextStep();
  };

  const handleDetailsAndSharesComplete = () => {
    markStepComplete("details-and-shares");
    nextStep();
  };

  // Track summary and submit step
  const [summaryAndSubmitValid, setSummaryAndSubmitValid] = useState(false);
  const [summaryAndSubmitFn, setSummaryAndSubmitFn] = useState<(() => Promise<void>) | null>(null);

  const handleSummaryAndSubmitValidation = (isValid: boolean, submitFn?: () => Promise<void>) => {
    setSummaryAndSubmitValid(isValid);
    setSummaryAndSubmitFn(() => submitFn || null);
  };

  const handleSummaryAndSubmitComplete = async () => {
    markStepComplete("summary-and-submit");
    console.log("Album wizard complete!");
  };

  // Add helper functions for Spotify URL handling
  const parseSpotifyUrl = (url: string): string | null => {
    const urlParts = url.split("/");
    const albumId = urlParts[urlParts.length - 1].split("?")[0];
    return albumId && albumId !== "" ? albumId : null;
  };

  // New form submit handler using react-hook-form
  const onFormSubmit = async (data: SpotifyAlbumFormData) => {
    setSpotifyURL(data.spotifyUrl);
    const albumId = parseSpotifyUrl(data.spotifyUrl);
    if (albumId === null || albumId === "") {
      form.setError("spotifyUrl", {
        type: "manual",
        message: "Invalid Spotify album URL. Please check the URL and try again.",
      });
      return;
    }
    await fetchAlbumData(data.spotifyUrl);
  };

  const removeSpotify = () => {
    setSpotifyURL("");
    setSpotifyResult(null);
    setAlbumData(null);
    setTracks([]);
    setErrorMsg(null);
    setShowIframeSkeleton(false);
    form.reset(); // Reset the form when removing spotify data
  };

  // Render current step content
  const renderStepContent = () => {
    switch (currentStep) {
      case "album-import":
        return (
          <>
            <div>

              <Heading as="h3">{t("album:step-1-import-album")}</Heading>
              <Text as="p">{t("album:connect-spotify-import-album")}</Text>
            </div>

            <div
              // style={{
              //   backgroundColor: isTunecore
              //     ? "#282828"
              //     : "#fff",
              // }}
              className={`flex flex-col gap-6 shadow-md ${isTunecore ? "text-white md:px-14 md:py-10 p-4 rounded-lg bg-tunecore-dark-element" : "bg-sentric-light-grey p-4 md:px-14 md:py-10 rounded-lg text-old-sentric-dark-gray"}`}
            >
              <div>
                <Text as="subtitle">
                  {t("album:import-from-spotify-header")}
                </Text>
                <Text>
                  {t("album:spotify-album-import-description")}
                  {" "}
                  <span>
                    <a
                      className="underline hover:scale-105"
                      href="https://support.spotify.com/us/artists/article/finding-your-artist-url/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t("album:how-to-find-album-url")}
                    </a>
                  </span>
                </Text>
              </div>
              <div>
                {!spotifyResult && (
                  <Form {...form}>
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        void form.handleSubmit(onFormSubmit)(e);
                      }}
                      className="flex flex-col gap-4"
                    >
                      <FormField
                        control={form.control}
                        name="spotifyUrl"
                        render={({ field }) => (
                          <FormItem>
                            <FormControl>
                              <FormInput
                                type="text"
                                placeholder={t("album:paste-spotify-album-url") ?? "Paste Spotify Album URL here"}
                                className="w-full font-medium "
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <Button
                        className={`self-end group ${
                          isTunecore
                            ? "text-white font-tunecore hover:text-tunecore-green"
                            : "bg-[#1ED760] hover:bg-[#1DB954]  text-black"
                        }`}
                        type="submit"
                        variant="darkSecondary"
                        icon={<IconInputSearch className={`${isTunecore ? "stroke-white group-hover:stroke-tunecore-green" : "stroke-black"}`} />}
                        disabled={isLoading}
                        label={isLoading ? t("album:fetching-album") : t("album:fetch-spotify-album")}
                      />
                    </form>
                  </Form>
                )}
                {spotifyResult && (
                  <div className="flex flex-col gap-4">
                    <div className="relative w-full min-h-[400px]">
                      {showIframeSkeleton ? (
                        <div className="w-full min-h-[400px] bg-black bg-opacity-10 backdrop-blur-xl rounded-lg animate-pulse flex flex-col">

                          <div className="flex items-center gap-4 p-6">

                            <div className="w-20 h-20 bg-black rounded-md bg-opacity-10 backdrop-blur-xl animate-pulse"></div>
                            <div className="flex-1">

                              <div className="w-3/4 h-4 mb-2 bg-black rounded-md bg-opacity-10 backdrop-blur-xl animate-pulse"></div>

                              <div className="w-1/2 h-3 bg-black rounded-md bg-opacity-10 backdrop-blur-xl animate-pulse"></div>
                            </div>

                          </div>

                          <div className="flex-1 p-6 pt-0">
                            <div className="space-y-6">
                              {Array.from({ length: 4 }, (_, index) => (
                                <div key={`track-skeleton-${index}`} className="flex items-center gap-4">

                                  <div className="w-6 h-4 bg-black rounded bg-opacity-10 backdrop-blur-xl animate-pulse"></div>

                                  <div className="flex-1 h-4 bg-black rounded bg-opacity-10 backdrop-blur-xl animate-pulse"></div>

                                  <div className="w-12 h-4 bg-black rounded bg-opacity-10 backdrop-blur-xl animate-pulse"></div>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Loading text */}
                          <div className="absolute inset-0 flex items-center justify-center bg-black rounded-lg bg-opacity-20">
                            <div className="flex items-center gap-2 font-medium text-black">
                              <div className="w-4 h-4 border-2 border-black rounded-full border-t-transparent animate-spin"></div>
                              {t("album:loading-spotify-preview")}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <>
                          <iframe
                            title="preview"
                            src={`https://open.spotify.com/embed/album/${spotifyResult.id}`}
                            height="152"
                            allow="encrypted-media"
                            className="w-full min-h-[400px]"
                          />
                        </>
                      )}
                    </div>
                    <Button
                      variant="darkSecondary"
                      onClick={removeSpotify}

                      className={`self-end group ${
                        isTunecore
                          ? "text-white font-tunecore hover:text-tunecore-green"
                          : "bg-[#1ED760] hover:bg-[#1DB954]  text-black"
                      }`}
                      icon={<IconBin className={`${isTunecore ? "stroke-white group-hover:stroke-tunecore-green" : "stroke-black"}`} />}
                      label={t("album:remove-selection")}
                    />
                  </div>
                )}
              </div>
            </div>

            {errorMsg !== null && errorMsg !== "" && (
              <Text as="p" className="mt-4 text-red-500">
                {t("album:error")}
                :
                {" "}
                {errorMsg}
              </Text>
            )}

            <AddAlbumWizardNavigation
              isTunecore={isTunecore}
              nextDisabled={!spotifyResult || !albumStore.albumData || tracks.length === 0 || isLoading || (errorMsg !== null && errorMsg !== "")}
              onNext={handleAlbumImportComplete}
              hidePrevious={true}
              nextLabel={t("album:continue-to-select-songs") ?? "Continue to Select Songs"}
            />
          </>
        );

      case "select-songs":
        return (
          <>
            <div>

              <Heading as="h3">{t("album:step-2-select-songs")}</Heading>
              <Text as="p">{t("album:choose-tracks-from-album")}</Text>
            </div>

            {tracks.length > 0 ? (
              <SelectSongsStep />
            ) : (
              <Text as="p">{t("album:no-tracks-found-or-loading")}</Text>
            )}

            <AddAlbumWizardNavigation
              isTunecore={isTunecore}
              nextDisabled={tracks.filter(t => t.isSelected).length === 0}
              onNext={handleSelectSongsComplete}
              nextLabel={t("album:continue-to-choose-writers") ?? "Continue to Choose Writers"}
            />
          </>
        );

      case "choose-writers":
        return (
          <>
            <div>
              <Heading as="h3">{t("album:step-3-choose-writers")}</Heading>
              <Text as="p">{t("album:select-create-writers-album")}</Text>
            </div>

            <ChooseWritersStep />

            <AddAlbumWizardNavigation
              isTunecore={isTunecore}
              nextDisabled={selectedWriters.length === 0}
              onNext={handleChooseWritersComplete}
              nextLabel={t("album:continue-to-assign-writers") ?? "Continue to Assign Writers"}
            />
          </>
        );

      case "assign-writers":
        return (
          <>
            <div>
              <Heading as="h3">{t("album:step-4-assign-writers")}</Heading>
              <Text as="p">{t("album:assign-selected-writers-each-song")}</Text>
            </div>

            <AssignWritersStep />

            <AddAlbumWizardNavigation
              isTunecore={isTunecore}
              nextDisabled={false} // Allow button to be enabled, validation will happen on click
              onNext={handleAssignWritersComplete}
              nextLabel={t("album:continue-to-song-details-shares") ?? "Continue to Song Details & Shares"}
            />
          </>
        );

      case "details-and-shares":
        return (
          <>
            <div>
              <Heading as="h3">{t("album:step-5-song-details-shares")}</Heading>
              <Text as="p">{t("album:set-song-details-publishing-shares")}</Text>
            </div>

            <DetailsAndSharesStep />

            <AddAlbumWizardNavigation
              isTunecore={isTunecore}
              nextDisabled={false}
              onNext={handleDetailsAndSharesComplete}
              nextLabel={t("album:continue-to-summary") ?? "Continue to Summary"}
            />
          </>
        );

      case "summary-and-submit":
        return (
          <>
            <div>
              <Heading as="h3">{t("album:step-6-review-submit")}</Heading>
              <Text as="p">{t("album:review-song-details-submit")}</Text>
            </div>

            <SummaryAndSubmitStep onValidationComplete={handleSummaryAndSubmitValidation} />

            <AddAlbumWizardNavigation
              isTunecore={isTunecore}
              nextDisabled={!summaryAndSubmitValid}
              onNext={async () => {
                if (summaryAndSubmitFn) {
                  try {
                    await summaryAndSubmitFn();
                    await handleSummaryAndSubmitComplete();
                  }
                  catch (error) {
                    console.error("Error during album submission:", error);
                  }
                }
                else {
                  console.error("Submission function not available");
                }
              }}
              nextLabel={t("album:create-all-songs") ?? "Create All Songs"}
            />
          </>
        );

      default:
        return null;
    }
  };

  return (
    <div className="flex flex-wrap w-full lg:min-h-screen-minus-header">
      <div
        className={`w-full ${
          // location is not defined, will be fixed later
          typeof location !== "undefined" && location.pathname === "/songs" ? "lg:p-6 p-4" : ""
        } ${
          newAddSongs
            ? ""
            : typeof location !== "undefined" && location.pathname === "/songs/create"
              ? "lg:min-h-screen-minus-header mx-4 lg:mx-12 xl:mx-20 2xl:mx-40 my-12"
              : "lg:p-6 p-4 lg:min-h-screen-minus-header"
        } ${isTunecore ? "text-white" : ""}`}
      >
        <div className="flex flex-row gap-4 mb-8">
          <Heading className="font-semibold" as="h3">
            {t("album:add-album")}
          </Heading>
        </div>

        {/* Main Content Area */}
        <div className={`flex flex-col gap-4 ${isTunecore ? "bg-tunecore-dark-element" : "bg-white"} rounded-lg p-6 shadow-md`}>
          {/* Wizard Progress */}
          <AddAlbumWizardStepProgress isTunecore={isTunecore} />
          {renderStepContent()}
        </div>
      </div>
    </div>
  );
}
